/*
 This service is the place where fontAwesome icons are  suggested to be called in a Async way and loaded via the
 fontAwesome API
 more info : https://fontawesome.com/how-to-use/with-the-api/

 methods:
 the configFontAwesome method add aply th configs passed in a dinamyc way,
 applying the properties that the svg-core
 use: https://fontawesome.com/how-to-use/with-the-api/setup/configuration
*/
import AsyncScriptService from 'services/loader-handler-services/async-script-service';
import utils from 'lib/utils';
import { layer, parse, icon, config as fortawesomeConfig } from '@fortawesome/fontawesome-svg-core';
import { camelCase } from 'lodash';
import faMetaData from 'lib/fa-metadata';
import HandlebarsExtensions from 'lib/view-helper';

const IconLoadService = {
  // Basic fontAwesomeApi config
  defaultStyle: 'far',
  configFontAwesome: function (properties) {
    for (const key in properties) {
      if (Object.prototype.hasOwnProperty.call(fortawesomeConfig, key)) {
        fortawesomeConfig[key] = properties[key];
      } else {
        console.error(
          `the property ${key} is not available as a fontAwesomeAPI property check it at \n https://fontawesome.com/how-to-use/with-the-api/setup/configuration`
        );
      }
    }
  },
  fullToShortStyleFormatter(prefix) {
    const mappings = {
      'fa-regular': 'far',
      'fa-solid': 'fas',
      'fa-duotone': 'fad',
      'fa-light': 'fal',
      'fa-thin': 'fat',
    };
    return mappings[prefix] || prefix;
  },
  formatIconUrl(prefix, iconName) {
    if (!faMetaData[this.fullToShortStyleFormatter(prefix)]) {
      return;
    }
    const packIconName = iconName && iconName.startsWith('fa-') ? iconName.replace(/^fa-/, '') : iconName;
    const icon =
      faMetaData[this.fullToShortStyleFormatter(prefix)].includes(packIconName) ||
      faMetaData['far'].includes(packIconName) ||
      faMetaData['fal'].includes(packIconName) ||
      faMetaData['fas'].includes(packIconName) ||
      faMetaData['fad'].includes(packIconName) ||
      faMetaData['fat'].includes(packIconName) ||
      undefined;
    if (!icon) {
      console.error(
        `error with ${prefix}, for ${iconName},  only available properties are Solid(fas), \n regular(far), \n light(fal) \n light(fat) \n light(fad) \n and brand(fab),`
      );
      return;
    }
    const faIconDirs = {
      far: 'pro-regular-svg-icons',
      fal: 'pro-light-svg-icons',
      fas: 'pro-solid-svg-icons',
      fad: 'pro-duotone-svg-icons',
      fat: 'pro-thin-svg-icons',
      'fa-regular': 'pro-regular-svg-icons',
      'fa-light': 'pro-light-svg-icons',
      'fa-solid': 'pro-solid-svg-icons',
      'fa-duotone': 'pro-duotone-svg-icons',
      'fa-thin': 'pro-thin-svg-icons',
    };
    const curatedIconName = camelCase(iconName);
    return {
      http: utils.coursePath(`javascripts/fontawesome/${faIconDirs[prefix]}/${curatedIconName}.js`),
      require: `@fortawesome/${faIconDirs[prefix]}/${curatedIconName}.js`,
    };
  },

  returnIconAsString(iconPrefix, iconName) {
    const urlIcon = this.formatIconUrl(iconPrefix, iconName);
    return AsyncScriptService.load(urlIcon)
      .then(([iconElem]) => {
        const iconSvg = icon(iconElem.definition);
        return iconSvg.node[0];
      })
      .catch(err => console.error(err));
  },

  applyIconList: function (iconArr) {
    iconArr.forEach(({ icon_prefix, icon_name, index_id, icon_class }) => {
      this.applyIcon(icon_name, index_id, icon_class, icon_prefix);
    });
  },

  applyIcon(iconName, iconId, customClasses, optionalPrefix = null) {
    const { name, prefix } = this.setPrefixAndName(iconName, optionalPrefix);
    try {
      const urlIcon = this.formatIconUrl(prefix, name);
      AsyncScriptService.load(urlIcon).then(([iconElem]) => {
        const propsIcon = {
          classes: [],
          transform: {},
        };
        if (customClasses) {
          if (typeof customClasses === 'string') {
            propsIcon.classes.push(customClasses);
          } else if (Array.isArray(customClasses)) {
            customClasses.forEach(element => {
              propsIcon.classes.push(element);
            });
          }
        }

        try {
          const iconParent = document.querySelector(`[data-icon="${iconId}-${prefix} ${name}"]`);
          if (!iconParent) {
            return;
          }
          if (iconParent.dataset.faTransform) {
            propsIcon.transform = parse.transform(iconParent.dataset.faTransform);
          }
          const iconSvg = icon(iconElem.definition, propsIcon);
          if (this.isIconParentEmpty(iconParent)) {
            iconParent.appendChild(iconSvg.node[0]);
          }
        } catch (e) {
          console.error(`Could not apply icon "${iconId}-${prefix} ${name}" by id`, e);
        }
      });
    } catch (ex) {
      console.error('Icon could not be loaded', ex);
    }
  },

  isIconParentEmpty(iconParent) {
    // to prevent adding multiple icons in the same parent container
    const { children, childElementCount } = iconParent;
    const srContent = childElementCount === 1 && children[0].classList.contains('screen-reader-content');
    return childElementCount === 0 || srContent;
  },

  stackIcons: function (icons, iconId) {
    const iconsUrl = [];
    const iconsClasses = {};
    const iconTransforms = {};
    icons.forEach(elem => {
      iconsUrl.push(this.formatIconUrl(elem.prefix, elem.name));
      if (Object.prototype.hasOwnProperty.call(elem, 'classes')) {
        iconsClasses[`${elem.prefix}-${elem.name}`] = elem.classes.split(' ');
      }
      if (Object.prototype.hasOwnProperty.call(elem, 'transforms') && elem.transforms) {
        iconTransforms[`${elem.prefix}-${elem.name}`] = parse.transform(elem.transforms);
      }
    });
    AsyncScriptService.load(iconsUrl).then(response => {
      const htmlElem = layer(push => {
        response.forEach(elem => {
          const classMapper = `${elem.definition.prefix}-fa-${elem.definition.iconName}`;
          const classes = Object.prototype.hasOwnProperty.call(iconsClasses, classMapper)
            ? iconsClasses[classMapper]
            : [];
          const transforms = Object.prototype.hasOwnProperty.call(iconTransforms, classMapper)
            ? iconTransforms[classMapper]
            : {};
          push(icon(elem.definition, { classes: classes, transform: transforms }));
        });
      });

      try {
        const iconParent = document.getElementById(iconId);
        if (iconParent && this.isIconParentEmpty(iconParent)) {
          iconParent.appendChild(htmlElem.node[0]);
        }
      } catch (e) {
        console.warn('Could not load icon by id', e);
      }
    });
  },
  setPrefixAndName(iconName, prefix = null) {
    if (prefix) {
      if (iconName.indexOf(' ') !== -1) {
        iconName = iconName.split(' ').pop();
      }
      return { prefix, name: iconName };
    } else {
      const [prefix, name] = iconName.split(' ').length > 1 ? iconName.split(' ') : [this.defaultStyle, iconName];
      return { prefix, name };
    }
  },
  getIconFromModularJaml(iconBlock) {
    const icon = IconLoadService.setPrefixAndName(iconBlock.icon);

    const transforms = [];
    const classes = [];
    const transformables = ['flip', 'horizontal', 'vertical', 'scale', 'rotate'];
    if (iconBlock.styles) {
      Object.entries(iconBlock.styles).forEach(([key, value]) => {
        if (transformables.includes(key)) {
          transforms.push(HandlebarsExtensions.helpers.faTransform(key, value));
        } else {
          classes.push(value);
        }
      });
    }
    icon.transforms = transforms.join(' ');
    icon.classes = classes.join(' ');
    return icon;
  },
};

export default IconLoadService;
